import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import { motion } from 'framer-motion'
import { type ReactNode, useState } from 'react'

export interface DetailSheetTab {
  id: string
  label: string
  content: ReactNode
  badge?: ReactNode
}

export interface DetailSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: ReactNode
  description?: ReactNode
  /** Plain-English explainer rendered above the tabs ("What is this?"). */
  whatIsThis?: ReactNode
  tabs: DetailSheetTab[]
  defaultTab?: string
  /** @deprecated kept for API compatibility — Dialog ignores side. */
  side?: 'right' | 'left'
  size?: 'default' | 'lg' | 'xl'
  footer?: ReactNode
  className?: string
}

const SIZE_CLASS = {
  default: 'sm:max-w-2xl',
  lg: 'sm:max-w-3xl',
  xl: 'sm:max-w-4xl'
} as const

/**
 * Modal detail view — Dialog popup (centered, with backdrop blur).
 * Used as the default surface for "click row → see details" across the admin.
 *
 * Keeps the original `DetailSheet` name to avoid touching the dozens of
 * callers; only the underlying primitive changed (Sheet → Dialog).
 */
export function DetailSheet({
  open,
  onOpenChange,
  title,
  description,
  whatIsThis,
  tabs,
  defaultTab,
  size = 'default',
  footer,
  className
}: DetailSheetProps) {
  const [active, setActive] = useState(defaultTab ?? tabs[0]?.id)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className={cn(
          'flex h-[80vh] w-[calc(100%-2rem)] flex-col gap-0 p-0',
          SIZE_CLASS[size],
          className
        )}
      >
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle className="text-base">{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        {tabs.length === 0 ? (
          <ScrollArea className="flex-1">
            <div className="px-6 py-4">
              {whatIsThis && (
                <p className="mb-4 rounded-lg border border-primary/15 bg-primary/[0.04] p-3 text-xs text-muted-foreground">
                  {whatIsThis}
                </p>
              )}
            </div>
          </ScrollArea>
        ) : (
          <Tabs
            value={active}
            onValueChange={setActive}
            className="flex flex-1 flex-col overflow-hidden"
          >
            <TabsList className="mx-6 mt-3 self-start">
              {tabs.map((t) => (
                <TabsTrigger key={t.id} value={t.id} className="gap-2">
                  {t.label}
                  {t.badge}
                </TabsTrigger>
              ))}
            </TabsList>

            {whatIsThis && (
              <p className="mx-6 mt-3 rounded-lg border border-primary/15 bg-primary/[0.04] p-3 text-xs text-muted-foreground">
                {whatIsThis}
              </p>
            )}

            <ScrollArea className="flex-1">
              {tabs.map((t) => (
                <TabsContent
                  key={t.id}
                  value={t.id}
                  className="mt-0 px-6 py-4 outline-none"
                  forceMount
                  hidden={active !== t.id}
                >
                  <motion.div
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.18, ease: 'easeOut' }}
                  >
                    {t.content}
                  </motion.div>
                </TabsContent>
              ))}
            </ScrollArea>
          </Tabs>
        )}

        {footer && <div className="border-t px-6 py-3">{footer}</div>}
      </DialogContent>
    </Dialog>
  )
}
